import { useMemo, useState } from 'react';
import { Link } from 'wouter';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  PieChart, Pie, Cell,
} from 'recharts';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { can } from '../lib/permissions';
import { assetClassLabel } from '../../../shared/portfolio';
import { periodStart, type PeriodKey } from '../../../shared/performance';
import { groupHoldings } from '../../../shared/exposures';
import { buildDataQualityIssues, dqCounts, type DataQualityIssue } from '../../../shared/portfolioDataQuality';
import { formatAUD, formatPriceAUD, formatUnits, formatPct, formatDateAU, isStale, daysOld, currentAuFy, fyLabel } from '../../../shared/format';
import { QAEmpty } from './QAEmpty';
import { QASkeleton } from './QASkeleton';
import { QAChartTooltip } from './QAChartTooltip';
import type {
  PerformanceResponse, CgtResponse, IncomeResponse, PortfolioSnapshot,
  PortfolioAccount, PortfolioHolding,
} from '../../../shared/api';
import type { AllocationSlice } from '../../../shared/portfolio';

const PERIODS: Array<[string, string]> = [
  ['1m', '1M'], ['3m', '3M'], ['6m', '6M'], ['1y', '1Y'], ['3y', '3Y'],
  ['5y', '5Y'], ['fytd', 'FYTD'], ['cytd', 'CYTD'], ['itd', 'ITD'],
];

const QA_COLOURS = ['#B8431A', '#A07A3F', '#2F4636', '#8A6418', '#7A6E63', '#3D3530'];

function pct(v: number | null | undefined): string {
  return v == null ? '—' : formatPct(v, 2);
}

/** Calendar days the active period *should* span as at `asOf` (0 = ITD/unbounded). */
function expectedDays(period: string, asOf: string): number {
  const start = periodStart(asOf, period as PeriodKey);
  if (!start) return 0;
  const ms = Date.parse(`${asOf}T00:00:00Z`) - Date.parse(`${start}T00:00:00Z`);
  return Number.isFinite(ms) ? Math.max(0, Math.round(ms / 86_400_000)) : 0;
}

/** Last value in a date-sorted series on or before `date` (fills calendar gaps). */
function valueOnOrBefore(series: Array<{ date: string; valueCents: number }>, date: string): number | null {
  let v: number | null = null;
  for (const p of series) { if (p.date <= date) v = p.valueCents; else break; }
  return v;
}

export function PerformanceTab({ clientId, portfolio }: { clientId: string; portfolio: PortfolioSnapshot }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canManage = can(user?.role, 'users.manage');
  const [selection, setSelection] = useState<string>('consolidated'); // 'consolidated' | accountId
  const [period, setPeriod] = useState<string>('itd');
  const [hSearch, setHSearch] = useState('');
  const [hSort, setHSort] = useState<{ key: 'value' | 'ugl' | 'ret'; dir: 'asc' | 'desc' }>({ key: 'value', dir: 'desc' });
  const [fy, setFy] = useState<number | 'all'>(currentAuFy());
  const [groupBySecurity, setGroupBySecurity] = useState(false);

  const base = selection === 'consolidated'
    ? `/api/clients/${clientId}`
    : `/api/clients/${clientId}/accounts/${selection}`;

  const perfQ = useQuery({ queryKey: ['perf', clientId, selection], queryFn: () => api<{ data: PerformanceResponse }>(`${base}/performance`) });
  const fyParam = fy === 'all' ? '' : `?fy=${fy}`;
  const cgtQ = useQuery({ queryKey: ['perf-cgt', clientId, selection, fy], queryFn: () => api<{ data: CgtResponse }>(`${base}/cgt${fyParam}`) });
  const incomeQ = useQuery({ queryKey: ['perf-income', clientId, selection, fy], queryFn: () => api<{ data: IncomeResponse }>(`${base}/income${fyParam}`) });

  // Pending feed-reconciliation count — admin-only, used to surface a badge
  // + link to /admin/feed-reconciliation. Stays quiet for non-managers.
  const reconcileQ = useQuery({
    queryKey: ['feed-pending-count'],
    queryFn: () => api<{ data: { count: number } }>('/api/admin/feed-reconciliation/pending-count'),
    enabled: canManage,
    staleTime: 60_000,
  });
  const pendingCount = reconcileQ.data?.data.count ?? 0;

  const refresh = useMutation({
    mutationFn: () => api('/api/integrations/market/refresh', { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['client', clientId, 'full'] });
      qc.invalidateQueries({ queryKey: ['perf'] });
      qc.invalidateQueries({ queryKey: ['perf-cgt'] });
      qc.invalidateQueries({ queryKey: ['perf-income'] });
    },
  });

  const sync = useMutation({
    mutationFn: () => api<{ data: { conflicts?: number } }>('/api/admin/feed-reconciliation/sync', { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['perf'] });
      qc.invalidateQueries({ queryKey: ['perf-cgt'] });
      qc.invalidateQueries({ queryKey: ['perf-income'] });
      qc.invalidateQueries({ queryKey: ['feed-pending-count'] });
    },
  });

  const perf = perfQ.data?.data;

  // Selected account, when one is chosen (vs consolidated).
  const selectedAccount: PortfolioAccount | null = useMemo(() => {
    if (selection === 'consolidated') return null;
    return portfolio.accounts.find((a) => a.id === selection) ?? null;
  }, [selection, portfolio]);

  const allocationSlices: AllocationSlice[] = useMemo(() => {
    const raw = selectedAccount ? selectedAccount.allocation : portfolio.allocation;
    return (raw ?? []).map((s) => ({ ...s, bucket: assetClassLabel(s.bucket) }));
  }, [selectedAccount, portfolio]);

  const geographySlices: AllocationSlice[] = useMemo(() => {
    return selectedAccount ? (selectedAccount.geography ?? []) : (portfolio.geography ?? []);
  }, [selectedAccount, portfolio]);

  const holdingRows: Array<PortfolioHolding & { accountName?: string }> = useMemo(() => {
    if (selectedAccount) return selectedAccount.holdings;
    return portfolio.accounts.flatMap((a) => a.holdings.map((h) => ({ ...h, accountName: a.name })));
  }, [selectedAccount, portfolio]);

  // Cashflow-adjusted TWR index, rebased to 100 at the selected period start —
  // so contributions/withdrawals don't read as performance (round 1 #1).
  const chartData = useMemo(() => {
    if (!perf) return [];
    const start = perf.periods[period]?.startDate ?? null;
    const allIdx = perf.indexSeries ?? [];
    let idx = allIdx;
    if (start) {
      const anchor = [...allIdx].reverse().find((p) => p.date <= start);
      const after = allIdx.filter((p) => p.date > start);
      idx = anchor ? [anchor, ...after] : allIdx.filter((p) => p.date >= start);
    }
    if (idx.length === 0) return [];
    const p0 = idx[0].index || 1;
    // Benchmark is rebased to its level on/before the chart start, and matched to
    // each portfolio date by "last value on or before" so differing calendars
    // (benchmark vs valuation) don't punch holes in the line.
    const bmSeries = perf.benchmark?.series ?? [];
    const bmStart = valueOnOrBefore(bmSeries, idx[0].date);
    return idx.map((p) => {
      const bmv = valueOnOrBefore(bmSeries, p.date);
      return {
        date: p.date,
        portfolio: Math.round((p.index / p0) * 1000) / 10,
        benchmark: bmv != null && bmStart ? Math.round((bmv / bmStart) * 1000) / 10 : null,
      };
    });
  }, [perf, period]);

  const canGroup = !selectedAccount;
  const displayedHoldings = useMemo(() => {
    // Group-by-security folds same-code rows across accounts into the flat shape
    // (Account column becomes "N accounts"); search + sort then apply as normal.
    const base: Array<PortfolioHolding & { accountName?: string }> = (canGroup && groupBySecurity)
      ? groupHoldings(
          holdingRows.map((h) => ({ code: h.code, name: h.name, assetClass: h.assetClass, accountName: h.accountName, units: h.units, currentValue: h.currentValue, costBase: h.costBase, unrealisedGainLoss: h.unrealisedGainLoss })),
          portfolio.tnw,
        ).map((g) => ({
          code: g.code, name: g.name ?? '', sector: null, assetClass: g.assetClass ?? '',
          units: g.units, costBase: g.costBase, currentPrice: g.currentPrice, currentValue: g.value,
          unrealisedGainLoss: g.gainLoss, unrealisedReturnPct: g.returnPct,
          accountName: g.accountNames.length === 1 ? g.accountNames[0] : `${g.accountNames.length} accounts`,
        }))
      : holdingRows;
    const q = hSearch.trim().toLowerCase();
    const filtered = q
      ? base.filter((h) => `${h.code} ${h.name} ${h.accountName ?? ''}`.toLowerCase().includes(q))
      : base;
    const val = (h: typeof filtered[number]) => hSort.key === 'value' ? h.currentValue : hSort.key === 'ugl' ? h.unrealisedGainLoss : h.unrealisedReturnPct;
    return [...filtered].sort((a, b) => (val(a) - val(b)) * (hSort.dir === 'asc' ? 1 : -1));
  }, [holdingRows, hSearch, hSort, canGroup, groupBySecurity, portfolio]);

  const toggleSort = (key: 'value' | 'ugl' | 'ret') =>
    setHSort((s2) => s2.key === key ? { key, dir: s2.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' });
  const ariaSort = (key: 'value' | 'ugl' | 'ret'): 'ascending' | 'descending' | 'none' =>
    hSort.key === key ? (hSort.dir === 'asc' ? 'ascending' : 'descending') : 'none';

  const holdingsSubtotal = useMemo(
    () => holdingRows.reduce((s, h) => s + h.currentValue, 0),
    [holdingRows],
  );
  const cashTotal = useMemo(
    () => (selectedAccount ? selectedAccount.cashBalance : portfolio.accounts.reduce((s, a) => s + a.cashBalance, 0)),
    [selectedAccount, portfolio],
  );

  const dqIssues = useMemo<DataQualityIssue[]>(() => buildDataQualityIssues({
    asOf: perf?.asOf ?? null,
    accounts: portfolio.accounts.map((a) => ({
      id: a.id, name: a.name, cashBalance: a.cashBalance, reportedValue: a.value,
      holdings: a.holdings.map((h) => ({ code: h.code, name: h.name, assetClass: h.assetClass, units: h.units, costBase: h.costBase, currentValue: h.currentValue })),
    })),
    coverageComplete: perf?.coverage?.complete ?? null,
    benchmarkConfigured: !!perf?.benchmark,
    pendingFeedConflicts: pendingCount,
  }), [perf, portfolio, pendingCount]);

  if (perfQ.isLoading) return <QASkeleton variant="cards" />;
  if (perfQ.isError || !perf) return <div className="qa-badge qa-badge--out mt-6">Unable to load performance</div>;

  const pr = perf.periods[period];
  const bmTwr = perf.benchmark?.periods[period]?.twr ?? null;
  const excess = pr?.twr != null && bmTwr != null ? pr.twr - bmTwr : null;

  return (
    <div className="mt-6 space-y-8">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex flex-wrap items-center gap-3">
          <select
            className="qa-input text-sm"
            value={selection}
            onChange={(e) => setSelection(e.target.value)}
            aria-label="Portfolio"
          >
            <option value="consolidated">Consolidated household</option>
            {portfolio.accounts.map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
          <select
            className="qa-input text-sm"
            value={String(fy)}
            onChange={(e) => setFy(e.target.value === 'all' ? 'all' : Number(e.target.value))}
            aria-label="Financial year for income and realised CGT"
            title="Financial year — income and realised CGT"
          >
            {[0, 1, 2].map((d) => { const y = currentAuFy() - d; return <option key={y} value={y}>{fyLabel(y)}</option>; })}
            <option value="all">All history</option>
          </select>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canManage && pendingCount > 0 ? (
            <Link to="/admin/feed-reconciliation"
              className="text-[11px] uppercase font-bold tracking-widest border border-rust text-rust px-2.5 py-1 hover:bg-rust hover:text-paper transition-colors"
              aria-label={`${pendingCount} pending feed conflicts — open reconciliation`}>
              Reconcile ({pendingCount})
            </Link>
          ) : null}
          {canManage ? (
            <button type="button" className="btn-ghost text-sm" disabled={sync.isPending} onClick={() => sync.mutate()}>
              {sync.isPending ? 'Syncing…' : 'Sync positions'}
            </button>
          ) : null}
          <button type="button" className="btn-ghost text-sm" disabled={refresh.isPending} onClick={() => refresh.mutate()} aria-busy={refresh.isPending}>
            {refresh.isPending ? 'Refreshing…' : 'Refresh prices'}
          </button>
        </div>
      </div>
      {(refresh.isError || refresh.isSuccess || sync.isError || sync.isSuccess) ? (
        <p className={`text-xs -mt-4 ${refresh.isError || sync.isError ? 'text-rust-deep' : 'text-forest'}`} role="status">
          {refresh.isError ? 'Price refresh failed — try again or check the market provider.'
            : sync.isError ? 'Position sync failed — try again or contact operations.'
            : sync.isSuccess ? `Positions synced${(sync.data?.data.conflicts ?? 0) > 0 ? ` — ${sync.data?.data.conflicts} conflict(s) to review` : ''}.`
            : 'Prices refreshed — valuations recalculating.'}
        </p>
      ) : null}

      {/* Period pills */}
      <div className="flex flex-wrap gap-2">
        {PERIODS.map(([k, label]) => (
          <button
            key={k}
            type="button"
            aria-pressed={period === k}
            aria-label={`Show ${label} performance`}
            onClick={() => setPeriod(k)}
            className={`px-2.5 py-1 text-[11px] uppercase font-bold tracking-widest border ${period === k ? 'border-rust text-rust' : 'border-hairline text-ink-mute hover:text-ink'}`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Data freshness + coverage (round 1 #2) */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-ink-mute -mt-3">
        <span className={isStale(perf.asOf) ? 'text-rust-deep font-semibold' : ''}>
          Valuations as at {formatDateAU(perf.asOf)}{isStale(perf.asOf) ? ` · ${daysOld(perf.asOf)}d old — refresh` : ''}
        </span>
        {pr?.startDate ? <span>History from {formatDateAU(pr.startDate)} · {pr.days}d</span> : null}
        {pr && pr.days > 366 ? <span className="text-ink-mute">TWR annualised · MWR is annualised (XIRR)</span> : null}
        {perf.coverage && !perf.coverage.complete ? (
          <span className="text-caution font-semibold">Some account valuations carried forward</span>
        ) : null}
        {pr && pr.days > 0 && expectedDays(period, perf.asOf) > 0 && pr.days < expectedDays(period, perf.asOf) * 0.8 ? (
          <span className="text-caution font-semibold">Partial period</span>
        ) : null}
      </div>

      {/* Return figures */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Return (TWR)" value={pct(pr?.twr)} note={pr?.days ? `${pr.days} days` : ''} />
        <Stat label="Return (MWR/IRR)" value={pct(pr?.mwr)} note={pr?.mwr != null ? 'annualised' : ''} />
        <Stat label="Benchmark (TWR)" value={pct(bmTwr)} note={perf.benchmark?.name ?? ''} />
        <Stat label="Excess vs benchmark" value={pct(excess)} tone={excess == null ? undefined : excess >= 0 ? 'pos' : 'neg'} />
      </div>

      {/* Benchmark composition (round 5 #2) */}
      {perf.benchmark && perf.benchmark.components.length > 0 ? (
        <details className="text-[11px] text-ink-mute -mt-4">
          <summary className="cursor-pointer select-none">
            Benchmark basis: {perf.benchmark.name} · {perf.benchmark.source === 'account_override' ? 'account override' : 'risk-profile default'}
          </summary>
          <p className="mt-1 qa-mono">
            {perf.benchmark.components.map((c) => `${c.indexCode} ${Math.round(c.weight * 100)}%`).join(' · ')}
          </p>
        </details>
      ) : null}

      {/* Data quality (round 4 #5) */}
      <DataQualityCard issues={dqIssues} />

      {/* Chart */}
      <div className="qa-card p-6">
        <p className="sr-only">
          {`Cashflow-adjusted growth of $100 for the selected ${period.toUpperCase()} period. Portfolio time-weighted return ${pct(pr?.twr)}; benchmark ${pct(bmTwr)}; excess ${pct(excess)}.`}
        </p>
        <p className="qa-eyebrow mb-3">Growth of $100 — cashflow-adjusted (TWR) vs benchmark</p>
        {chartData.length < 2 ? (
          <QAEmpty inline title="Not enough valuation history yet.">The nightly valuation job builds this series over time.</QAEmpty>
        ) : (
          <div className="h-72">
            <ResponsiveContainer>
              <LineChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                <CartesianGrid stroke="rgba(26,22,20,0.08)" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => formatDateAU(String(d))} minTickGap={40} />
                <YAxis tick={{ fontSize: 11 }} width={40} />
                <Tooltip content={<QAChartTooltip valueFormat={(v) => (typeof v === 'number' ? v.toFixed(1) : String(v))} />} />
                <Legend />
                <Line type="monotone" dataKey="portfolio" name="Portfolio" stroke="#B8431A" dot={false} strokeWidth={2} connectNulls />
                <Line type="monotone" dataKey="benchmark" name="Benchmark" stroke="#A07A3F" dot={false} strokeWidth={1.5} strokeDasharray="4 3" connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Allocation + geography pies (scoped to selection) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <AllocationCard title="Asset allocation" slices={allocationSlices} />
        <AllocationCard title="Geography" slices={geographySlices} />
      </div>

      {/* Holdings (scoped to selection) */}
      <div className="qa-card overflow-hidden">
        <header className="px-5 py-3 flex flex-wrap items-center gap-3 justify-between">
          <p className="qa-eyebrow">{selectedAccount ? `Holdings — ${selectedAccount.name}` : 'Holdings — all accounts'}</p>
          <div className="flex items-center gap-3">
            {canGroup ? (
              <button
                type="button"
                aria-pressed={groupBySecurity}
                onClick={() => setGroupBySecurity((g) => !g)}
                className={`text-[11px] uppercase font-bold tracking-widest border px-2.5 py-1 ${groupBySecurity ? 'border-rust text-rust' : 'border-hairline text-ink-mute hover:text-ink'}`}
              >
                Group by security
              </button>
            ) : null}
            <input
              type="search"
              value={hSearch}
              onChange={(e) => setHSearch(e.target.value)}
              placeholder="Search code / name…"
              aria-label="Search holdings"
              className="qa-input text-xs py-1 w-44"
            />
            <span className="text-xs text-ink-mute qa-mono">{displayedHoldings.length === holdingRows.length ? `${holdingRows.length} positions` : `${displayedHoldings.length} of ${holdingRows.length}`}</span>
          </div>
        </header>
        {holdingRows.length === 0 ? (
          <div className="px-5 pb-5"><QAEmpty inline title="No holdings to show." /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="qa-table">
              <thead>
                <tr>
                  <th>Security</th>
                  {!selectedAccount && <th>{groupBySecurity ? 'Held in' : 'Account'}</th>}
                  <th>Class</th>
                  <th className="text-right">Units</th>
                  <th className="text-right">Price</th>
                  <th className="text-right" aria-sort={ariaSort('value')}>
                    <button type="button" className="underline-offset-2 hover:underline" onClick={() => toggleSort('value')}>Value{hSort.key === 'value' ? (hSort.dir === 'asc' ? ' ↑' : ' ↓') : ''}</button>
                  </th>
                  <th className="text-right" aria-sort={ariaSort('ugl')}>
                    <button type="button" className="underline-offset-2 hover:underline" onClick={() => toggleSort('ugl')}>U/G/L{hSort.key === 'ugl' ? (hSort.dir === 'asc' ? ' ↑' : ' ↓') : ''}</button>
                  </th>
                  <th className="text-right" aria-sort={ariaSort('ret')}>
                    <button type="button" className="underline-offset-2 hover:underline" onClick={() => toggleSort('ret')}>Return{hSort.key === 'ret' ? (hSort.dir === 'asc' ? ' ↑' : ' ↓') : ''}</button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {displayedHoldings.map((h, i) => (
                  <tr key={`${h.code}-${i}`}>
                    <td><span className="font-medium">{h.code}</span> <span className="text-ink-mute">{h.name}</span></td>
                    {!selectedAccount && <td className="text-xs text-ink-mute">{h.accountName}</td>}
                    <td className="text-xs text-ink-mute">{assetClassLabel(h.assetClass)}</td>
                    <td className="num">{formatUnits(h.units)}</td>
                    <td className="num">{formatPriceAUD(h.currentPrice)}</td>
                    <td className="num">{formatAUD(h.currentValue)}</td>
                    <td className={`num ${h.unrealisedGainLoss >= 0 ? 'text-forest' : 'text-rust-deep'}`}>
                      {h.unrealisedGainLoss >= 0 ? '+' : ''}{formatAUD(h.unrealisedGainLoss)}
                    </td>
                    <td className={`num ${h.unrealisedGainLoss >= 0 ? 'text-forest' : 'text-rust-deep'}`}>
                      {h.unrealisedReturnPct >= 0 ? '+' : ''}{formatPct(h.unrealisedReturnPct)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="qa-hairline-t">
                  <td colSpan={selectedAccount ? 4 : 5} className="text-ink-mute">Holdings subtotal</td>
                  <td className="num">{formatAUD(holdingsSubtotal)}</td>
                  <td></td><td></td>
                </tr>
                <tr>
                  <td colSpan={selectedAccount ? 4 : 5} className="text-ink-mute">Cash</td>
                  <td className="num">{formatAUD(cashTotal)}</td>
                  <td></td><td></td>
                </tr>
                <tr className="font-medium">
                  <td colSpan={selectedAccount ? 4 : 5}>{selectedAccount ? 'Account value' : 'Total portfolio value'}</td>
                  <td className="num">{formatAUD(holdingsSubtotal + cashTotal)}</td>
                  <td></td><td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* CGT + income */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="qa-card p-6">
          <p className="qa-eyebrow mb-3">Capital gains <span className="text-ink-mute normal-case">· realised {fy === 'all' ? 'all history' : fyLabel(fy)} · unrealised as at {formatDateAU(perf.asOf)}</span></p>
          {cgtQ.isLoading ? <QASkeleton variant="cards" /> : cgtQ.data ? (
            <CgtPanel cgt={cgtQ.data.data} />
          ) : (
            <QAEmpty inline title="Unable to load capital gains">
              <button type="button" className="btn-ghost text-xs mt-1" onClick={() => cgtQ.refetch()}>Retry</button>
            </QAEmpty>
          )}
        </div>
        <div className="qa-card p-6">
          <p className="qa-eyebrow mb-3">Income &amp; franking <span className="text-ink-mute normal-case">· {fy === 'all' ? 'all history' : fyLabel(fy)}</span></p>
          {incomeQ.isLoading ? <QASkeleton variant="cards" /> : incomeQ.data ? (
            <IncomePanel income={incomeQ.data.data} />
          ) : (
            <QAEmpty inline title="Unable to load income">
              <button type="button" className="btn-ghost text-xs mt-1" onClick={() => incomeQ.refetch()}>Retry</button>
            </QAEmpty>
          )}
        </div>
      </div>
    </div>
  );
}

function AllocationCard({ title, slices }: { title: string; slices: AllocationSlice[] }) {
  if (!slices || slices.length === 0) {
    return (
      <div className="qa-card p-6">
        <p className="qa-eyebrow mb-3">{title}</p>
        <QAEmpty inline title="No data yet." />
      </div>
    );
  }
  return (
    <div className="qa-card p-6">
      <p className="qa-eyebrow mb-3">{title}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
        <div className="h-48">
          <ResponsiveContainer>
            <PieChart>
              <Pie data={slices} dataKey="value" nameKey="bucket" innerRadius={45} outerRadius={80} stroke="#FBF7EF">
                {slices.map((_, i) => (
                  <Cell key={i} fill={QA_COLOURS[i % QA_COLOURS.length]} />
                ))}
              </Pie>
              <Tooltip content={<QAChartTooltip valueFormat={(v, entry) => `${formatAUD(Number(v))}${entry.payload?.pct != null ? ` · ${formatPct(entry.payload.pct as number)}` : ''}`} />} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <ul className="space-y-1 text-sm">
          {slices.map((slice, i) => (
            <li key={slice.bucket} className="flex justify-between qa-mono">
              <span className="inline-flex items-center gap-2">
                <span className="inline-block w-2 h-2" style={{ background: QA_COLOURS[i % QA_COLOURS.length] }} />
                {slice.bucket}
              </span>
              <span>{formatPct(slice.pct)}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function Stat({ label, value, note, tone }: { label: string; value: string; note?: string; tone?: 'pos' | 'neg' }) {
  const colour = tone === 'pos' ? 'text-forest' : tone === 'neg' ? 'text-rust' : 'text-ink';
  return (
    <div className="qa-card p-4">
      <p className="qa-eyebrow">{label}</p>
      <p className={`text-2xl font-display mt-1 ${colour}`} style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</p>
      {note ? <p className="text-[11px] text-ink-mute mt-0.5">{note}</p> : null}
    </div>
  );
}

function Row({ label, value, tone }: { label: string; value: string; tone?: 'pos' | 'neg' }) {
  const colour = tone === 'pos' ? 'text-forest' : tone === 'neg' ? 'text-rust' : 'text-ink';
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-hairline last:border-0">
      <span className="text-sm text-ink-mute">{label}</span>
      <span className={`text-sm font-mono ${colour}`}>{value}</span>
    </div>
  );
}

function CgtPanel({ cgt }: { cgt: CgtResponse }) {
  const u = cgt.unrealised.summary;
  const r = cgt.realised.summary;
  return (
    <div>
      <p className="text-[11px] uppercase tracking-widest text-ink-mute mb-1">Unrealised</p>
      <Row label="Market value" value={formatAUD(u.marketCents / 100)} />
      <Row label="Cost base" value={formatAUD(u.costCents / 100)} />
      <Row label="Gain / loss" value={formatAUD(u.gainCents / 100)} tone={u.gainCents >= 0 ? 'pos' : 'neg'} />
      <Row label="Gain on parcels held >12 months" value={formatAUD(u.discountEligibleGainCents / 100)} />
      <p className="text-[11px] uppercase tracking-widest text-ink-mute mt-4 mb-1">Realised</p>
      <Row label="Net gain / loss" value={formatAUD(r.gainCents / 100)} tone={r.gainCents >= 0 ? 'pos' : 'neg'} />
      <Row label="Discount-eligible gains" value={formatAUD(r.discountedGainCents / 100)} />
      <Row label="Other gains (no discount)" value={formatAUD(r.otherGainCents / 100)} />
      <Row label="Capital losses" value={formatAUD(Math.abs(r.lossCents) / 100)} tone={r.lossCents < 0 ? 'neg' : undefined} />
      {cgt.entity ? (
        <>
          {cgt.entity.estimatedDiscountAmountCents != null ? (
            <Row label="Est. CGT discount applied" value={formatAUD(cgt.entity.estimatedDiscountAmountCents / 100)} />
          ) : null}
          <p className="text-[10px] text-ink-mute mt-2 leading-snug">{cgt.entity.label}. Estimate only — confirm tax treatment with the client's accountant.</p>
        </>
      ) : null}
    </div>
  );
}

function IncomePanel({ income }: { income: IncomeResponse }) {
  const s = income.summary;
  return (
    <div>
      <Row label="Cash income" value={formatAUD(s.cashAudCents / 100)} />
      <Row label="Franking credits" value={formatAUD(s.frankingCreditsAudCents / 100)} />
      <Row label="Grossed-up total" value={formatAUD(s.grossedUpAudCents / 100)} tone="pos" />
      <Row label="Events" value={String(s.count)} />
      {income.events.length > 0 && (
        <ul className="mt-3 text-xs text-ink-mute space-y-1">
          {income.events.slice(0, 6).map((e, i) => (
            <li key={i} className="flex justify-between">
              <span>{e.payDate ? formatDateAU(e.payDate) : '—'} · {e.securityCode ?? e.incomeType}</span>
              <span className="font-mono">{formatAUD(e.cashAudCents / 100)}{e.frankingCreditsAudCents > 0 ? ` (+${formatAUD(e.frankingCreditsAudCents / 100)} fr)` : ''}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function DataQualityCard({ issues }: { issues: DataQualityIssue[] }) {
  if (issues.length === 0) {
    return (
      <div className="qa-card px-5 py-3 flex items-center justify-between" role="status">
        <p className="qa-eyebrow">Data quality</p>
        <span className="text-xs text-forest font-medium">No material data exceptions detected</span>
      </div>
    );
  }
  const c = dqCounts(issues);
  const rule: Record<DataQualityIssue['severity'], string> = { high: 'border-rust-deep', medium: 'border-rust', info: 'border-brass' };
  return (
    <div className="qa-card overflow-hidden" role="region" aria-label="Data quality exceptions">
      <header className="px-5 py-3 flex items-baseline justify-between">
        <p className="qa-eyebrow">Data quality</p>
        <span className="text-xs qa-mono text-ink-mute">
          {c.blocking > 0 ? `${c.blocking} blocker${c.blocking === 1 ? '' : 's'} · ` : ''}{c.medium} warning{c.medium === 1 ? '' : 's'}{c.info ? ` · ${c.info} info` : ''}
        </span>
      </header>
      <ul className="divide-y divide-hairline">
        {issues.map((i) => (
          <li key={i.id} className={`pl-5 pr-5 py-2 border-l-2 ${rule[i.severity]}`}>
            <p className="text-xs font-semibold text-ink leading-tight">
              <span className="sr-only">{i.severity} severity: </span>{i.title}
            </p>
            <p className="text-[11px] text-ink-mute leading-snug">{i.detail}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}
